// Class-based error hierarchy for the expression engine.
//
// Pattern mirrors `packages/plugin-chat/src/lib/tool/errors/`: one abstract
// base, intermediate abstracts by failure category, concrete leaves with
// structured fields and a `suggestions()` recovery method. Adapted for two
// engine-specific concerns absent from tool errors:
//
// - **Source positions.** Every leaf carries a `pos: SourcePos` because
//   parse and eval failures both originate at a specific point in the
//   source (or `NO_POS` for direct JS-object input). `pos` is owned by the
//   base class — concrete leaves accept it as the trailing constructor arg
//   and pass it through `super(message, pos)`.
// - **Aggregation.** `Promise.allSettled` over sibling args can produce
//   multiple failures from a single op call. `AggregateExprError`
//   wraps them; callers flatten on demand via `flatten()`.

import type { z } from 'zod';

import { closestMatches } from '#core/lib/utils/closest-matches.ts';
import { StructuredError } from '#core/lib/utils/structured-error.ts';

import { NO_POS, type SourcePos } from './ast.ts';
import { astShape, opCallPos, type Spans } from './normalize.ts';
import { joinPath } from './path.ts';
import { relaxSymbols, zdef } from './schema/sym-codec.ts';
import { SCALAR_KINDS } from './schema/types.ts';

// Base.

/**
 * Abstract base for every error thrown by the expression engine. Subclasses
 * are concrete: instantiate them directly, throw, and catch via `instanceof`.
 *
 * Recovery hints live in `suggestions()` — a `readonly string[]` of
 * imperative sentences aimed at both humans and LLMs. The default is empty.
 * Subclasses with a single fixed hint (or one templated from constructor
 * fields) just set `staticHint`; the base reads it. Subclasses that compute
 * hints dynamically (`UnknownNameError`, `InvalidTokenError`) override
 * `suggestions()` directly.
 */
export abstract class ExprError extends StructuredError {
  /**
   * Set by subclasses that have one fixed recovery hint. The base
   * `suggestions()` returns `[staticHint]` when set, `[]` otherwise.
   * Excluded from `details()` via {@link reservedDetailKeys} — it's an
   * internal mechanism, not a structural field worth surfacing.
   */
  protected readonly staticHint?: string;

  constructor(
    message: string,
    readonly pos: SourcePos,
  ) {
    super(message);
  }

  /** `staticHint` is an internal `suggestions()` mechanism, not a structural field. */
  protected override reservedDetailKeys(): readonly string[] {
    return [...super.reservedDetailKeys(), 'staticHint'];
  }

  /** Recovery suggestions for callers / LLMs. */
  override suggestions(): readonly string[] {
    return this.staticHint === undefined ? [] : [this.staticHint];
  }

  /** `line:col: message` — compact rendering convenient for logs. */
  override format(): string {
    return `${this.pos.line}:${this.pos.col}: ${this.message}`;
  }
}

/**
 * Shared "did-you-mean" suggestion shape. Returns either an empty-state
 * hint (when no names are in scope) or one-to-two sentences: a
 * fuzzy-matched "Did you mean …?" line (fuse.js Bitap-ranked, top 3
 * under a tolerant match score; 0 = exact, 1 = anything) when at least
 * one candidate is close to the typed name, followed by the full
 * available list.
 *
 * The fuzzy-match line is what turns an unknown-name diagnostic from
 * a passive "here's everything we have" into an actionable suggestion
 * for the typed `name` — typo'd `actve` surfaces `active` immediately
 * instead of being buried in a long list of unrelated columns.
 */
const availableNamesSuggestion = (
  typed: string,
  emptyHint: string,
  listPrefix: string,
  names: readonly string[],
): readonly string[] => {
  if (names.length === 0) {
    return [emptyHint];
  }
  const suggestions = closestMatches(typed, names);
  const list = `${listPrefix}: ${names.join(', ')}.`;
  return suggestions.length === 0 ? [list] : [`Did you mean: ${suggestions.join(', ')}?`, list];
};

/**
 * The user-facing scalar column kinds named in recovery hints. Re-exported
 * straight from the {@link SCALAR_KINDS} SSOT so the prose and `sort.ts`'s
 * runtime `SORTABLE_KINDS` (derived from this, plus the internal `nullable`
 * kind) can never drift from what the sort check actually accepts.
 */
export const SCALAR_HINT_KINDS = SCALAR_KINDS;

/**
 * Shared recovery phrasing for "this column kind has no usable scalar
 * shape — project it first" failures. Canonical wording reused by the
 * sort / unnest / reference column-type errors so the "derive to a
 * scalar" guidance can't diverge across throw sites.
 */
const scalarProjectionHint = (column: string): string =>
  `Project \`${column}\` to a scalar first: add a \`derive\` (or time-bucket aggregate) producing a ` +
  `${SCALAR_HINT_KINDS.join(' / ')} column, then sort/reference on that derived column.`;

/**
 * Recovery phrasing for a column that has no orderable value AND no sortable
 * subfield — a `nested-dataset` / `timeseries` handle, an `array`, or a struct
 * whose subfields are themselves non-scalar. There is nothing on this column to
 * order by, so the fix is to sort by a different scalar column entirely (or, for
 * a timeseries/datapoints handle, resolve it first and sort the result).
 */
const pickDifferentColumnHint = (column: string): string =>
  `\`${column}\` has no orderable value and no sortable subfield, so nothing on it can be a sort key. ` +
  `Sort by a different scalar (${SCALAR_HINT_KINDS.join(' / ')}) column instead. For a timeseries/datapoints ` +
  'handle, resolve it first (time-bucket then unnest) and sort the resulting scalar column.';

/**
 * The canonical multi-column `unnest` `:as` recipe, shared by the collision and
 * not-nested hints so the arity guidance shown to the agent can't drift between
 * the two throw sites (mirrors the shared-phrasing role of the hints above).
 */
export const UNNEST_MULTI_COL_AS_HINT =
  '`:as` takes the shared key first, then one fresh name per column — e.g. `:as [:day :oil_d :gas_d :water_d]`.';

/**
 * Shared recovery phrasing for a struct/relation column used where a scalar
 * is required (sort key, join `:on` key, group key). Names a concrete,
 * runnable subfield reference drawn from the actual schema, lists the legal
 * subfields, and spells out the rename trap — a renamed struct is still a
 * struct, so renaming-and-retrying never helps. Reused across the sort and
 * join throw sites so the wording can't diverge.
 */
/**
 * The representative scalar subfield to show in a `(col … <subfield>)`
 * example — `externalId` (the relation identity key, always present and
 * always a valid sort/join key) when available, else the first scalar
 * subfield, else `externalId` as a safe placeholder. SSOT for the example
 * used by both {@link relationColumnHint} and the join `:on` error message.
 */
export const preferredSubfield = (scalarSubfields: readonly string[]): string =>
  scalarSubfields.includes('externalId') ? 'externalId' : (scalarSubfields[0] ?? 'externalId');

export const relationColumnHint = (
  column: string,
  scalarSubfields: readonly string[] = [],
  usage: 'join' | 'sort' = 'join',
): string => {
  const example = preferredSubfield(scalarSubfields);
  const list = scalarSubfields.length > 0 ? ` Available scalar subfields: ${scalarSubfields.join(', ')}.` : '';
  // A `(col …)` ref is a valid join `:on` key but NOT a valid sort key
  // (sort keys are keywords / `[:col :dir]` / `#(…)` lambdas), so the
  // concrete example must match the op that rejected the column.
  const fix =
    usage === 'join'
      ? `Reference a scalar subfield as the key, e.g. \`(col ${column} ${example})\`.`
      : `Key on a scalar subfield via a computed key, e.g. \`(sort ds [#(col % ${column} ${example}) :desc])\`, or \`derive\` it to a scalar column first.`;
  return (
    `\`${column}\` is a struct/relation column — it identifies a linked instance and has no scalar order. ` +
    `${fix}${list} ` +
    'Renaming the column does NOT change its type — a renamed struct is still a struct, so rename-and-retry will fail identically.'
  );
};

// Aggregation.

/**
 * Container error wrapping multiple sibling failures. Thrown when
 * `Promise.allSettled` over arg evaluations finds 2+ branches errored.
 * The tree structure is preserved as-given; callers call `flatten()` to
 * materialise the leaf list (which transparently descends into nested
 * aggregates).
 */
export class AggregateExprError extends ExprError {
  constructor(readonly errors: readonly ExprError[]) {
    super(`${errors.length} errors during evaluation`, errors[0]?.pos ?? NO_POS);
  }

  /** Walk the tree, returning a flat list of leaf errors (non-aggregate). */
  flatten(): readonly ExprError[] {
    return this.errors.flatMap((e) => (e instanceof AggregateExprError ? e.flatten() : [e]));
  }

  /**
   * Surface each leaf's recovery guidance, leaf-attributed and de-duplicated.
   * Without this, the aggregate path silently drops the "Did you mean …?" /
   * "pick one of the listed columns" hints that the single-error path
   * (`throwErrors`) hands straight to the agent — the cause of repeated
   * hallucinated-column retries when several column refs fail in one call.
   */
  override suggestions(): readonly string[] {
    // `Set` de-duplicates while preserving first-seen order, so guidance
    // shared across leaves (e.g. an identical "pick a listed column" line)
    // appears once, leaf order intact.
    return [...new Set(this.flatten().flatMap((e) => e.suggestions()))];
  }

  override details(): Readonly<Record<string, unknown>> {
    return {
      pos: this.pos,
      errors: this.flatten().map((e) => ({ kind: e.constructor.name, ...e.details(), suggestions: e.suggestions() })),
    };
  }
}

// Parser errors.

/** Abstract intermediate for every parse-time error. */
export abstract class ParseError extends ExprError {}

export class UnclosedListError extends ParseError {
  constructor(pos: SourcePos) {
    super('unclosed list — expected )', pos);
  }
}

export class UnclosedVectorError extends ParseError {
  constructor(pos: SourcePos) {
    super('unclosed vector — expected ]', pos);
  }
}

/**
 * A `)` with no matching `(`. Parsing stops at the first unmatched
 * closer, which may itself be the surplus (a trailing over-close — the
 * common case) or a leftover from an extra `)` earlier that closed an
 * enclosing form too soon. Rather than guess one theory, the diagnostic
 * is balance-aware: `imbalance` is the net `)` − `(` count over the whole
 * expression, `context` a source window around the blamed position
 * (`… token) …`), and `balanced` — when the surplus is an unambiguous
 * trailing run — a ready-to-resubmit corrected expression. `balanced` is
 * kept out of `details()` (it duplicates the suggestion text).
 */
export class StrayRparenError extends ParseError {
  constructor(
    pos: SourcePos,
    readonly context?: string,
    readonly imbalance?: number,
    readonly balanced?: string,
  ) {
    super('unmatched closing )', pos);
  }

  protected override reservedDetailKeys(): readonly string[] {
    return [...super.reservedDetailKeys(), 'balanced'];
  }

  override suggestions(): readonly string[] {
    const imbalanceHint =
      this.imbalance !== undefined && this.imbalance > 0
        ? ` This expression has ${this.imbalance} more \`)\` than \`(\` — remove the surplus closing paren(s), or add the missing opening one(s).`
        : '';
    const where = this.context === undefined ? '' : ` Fails around: \`${this.context}\`.`;
    const fix = this.balanced === undefined ? '' : ` A balanced version to resubmit: \`${this.balanced}\``;
    return [
      `Unbalanced parentheses — a \`)\` has no matching \`(\`.${imbalanceHint} Parsing stopped at the first unmatched \`)\` (see the position); the surplus may be there or earlier, so re-count delimiters left to right — including inside \`#(…)\` lambdas and \`(case …)\` clauses — and resubmit balanced.${where}${fix}`,
    ];
  }
}

export class StrayRbracketError extends ParseError {
  constructor(pos: SourcePos) {
    super('unmatched closing ]', pos);
  }
}
export class StrayRbraceError extends ParseError {
  constructor(pos: SourcePos) {
    super('unmatched closing }', pos);
  }
}

export class UnclosedMapError extends ParseError {
  constructor(pos: SourcePos) {
    super('unclosed map — expected }', pos);
  }
}

export class MapKeyNotKeywordError extends ParseError {
  protected override readonly staticHint =
    'Map literal keys must be keywords (e.g. {:name "Alice"}). For arbitrary-character keys use (hash-map "key" value …).';
  constructor(
    readonly got: string,
    pos: SourcePos,
  ) {
    super(`map literal key must be a keyword, got ${got}`, pos);
  }
}

export class MapMissingValueError extends ParseError {
  protected override readonly staticHint = 'Each key in a map literal must be followed by a value: {:k1 v1 :k2 v2 …}.';
  constructor(
    readonly key: string,
    pos: SourcePos,
  ) {
    super(`map literal key :${key} has no value`, pos);
  }
}

export class NestedHashFnError extends ParseError {
  protected override readonly staticHint =
    'Nested #() reader macros are not supported. Use (fn [arg] …) when a closure needs nesting.';
  constructor(pos: SourcePos) {
    super('nested #() is not supported', pos);
  }
}

export class HashFnEmptyError extends ParseError {
  protected override readonly staticHint = 'Reader macro #() requires a body: #(expr-using-% or %1 …).';
  constructor(pos: SourcePos) {
    super('empty #() reader macro', pos);
  }
}

export class HashFnArgGapError extends ParseError {
  protected override readonly staticHint =
    'Numbered #() args must be contiguous from %1 (e.g. %1 %2 %3). Skipping numbers leaves an undeclared parameter.';
  constructor(
    readonly missing: number,
    pos: SourcePos,
  ) {
    super(`#() body references %${missing + 1} but not %${missing}`, pos);
  }
}

export class HashFnMixedArgError extends ParseError {
  protected override readonly staticHint =
    'Use either bare % (single arg) or numbered %1 %2 …; do not mix the two forms in one #().';
  constructor(pos: SourcePos) {
    super('#() body mixes bare % with numbered %1/%2/…', pos);
  }
}

export class EmptyListError extends ParseError {
  protected override readonly staticHint = 'Lists are op calls; write (op-name ...args).';
  constructor(pos: SourcePos) {
    super('empty list — every list must have an op symbol as its head', pos);
  }
}

export class ListHeadNotSymbolError extends ParseError {
  protected override readonly staticHint = 'Lists are op calls; the first element must be a symbol naming the op.';
  constructor(
    readonly headDescription: string,
    pos: SourcePos,
  ) {
    super(`list head must be an op symbol, got ${headDescription}`, pos);
  }
}

export class ReservedWordError extends ParseError {
  constructor(
    readonly word: string,
    pos: SourcePos,
  ) {
    super(`reserved word "${word}" cannot be used as a keyword name`, pos);
  }
}

export class UnterminatedStringError extends ParseError {
  constructor(pos: SourcePos) {
    super('unterminated string literal', pos);
  }
}

export class UnknownEscapeError extends ParseError {
  protected override readonly staticHint = 'Valid escapes: \\" \\\\ \\n \\t \\r \\uXXXX';
  constructor(
    readonly escapeSequence: string,
    pos: SourcePos,
  ) {
    super(`unknown escape sequence \\${escapeSequence}`, pos);
  }
}

export class InvalidNumberError extends ParseError {
  protected override readonly staticHint =
    'Numbers are: integers, decimals, scientific (1.5e6), with optional `_` separators (1_000_000) and optional leading `-`.';
  constructor(
    readonly literal: string,
    pos: SourcePos,
  ) {
    super(`invalid number literal "${literal}"`, pos);
  }
}

export class InvalidTokenError extends ParseError {
  constructor(
    readonly token: string,
    pos: SourcePos,
  ) {
    super(`invalid token "${token}"`, pos);
  }
  override suggestions(): readonly string[] {
    if (this.token.includes('.')) {
      return ['Dots are not allowed in symbols. For cross-dataset column references, use (col <dataset> <column>).'];
    }
    return [];
  }
}

export class EmptyKeywordError extends ParseError {
  protected override readonly staticHint = 'Use a name like :filter, :group-by, :as.';
  constructor(pos: SourcePos) {
    super('keyword must have an identifier after the colon', pos);
  }
}

export class UnexpectedEofError extends ParseError {
  constructor(
    readonly context: string,
    pos: SourcePos,
  ) {
    super(context, pos);
  }
}

// Evaluator errors.

/**
 * Abstract intermediate for every evaluation-time error. Named
 * `EvaluationError` (not `EvalError`) to avoid shadowing the JS global.
 */
export abstract class EvaluationError extends ExprError {}

/**
 * Abstract base for the four "name not in scope" errors —
 * `UnknownOpError`, `UnboundSymbolError`, `UnknownColumnRefError`,
 * `UnknownViewError`. Each encodes the same shape: a user-supplied
 * identifier (`typed`) was referenced that isn't in this scope's
 * name table. Subclasses supply the message template plus the
 * empty-state hint and list prefix used to render
 * `availableNamesSuggestion`. Subclasses stay distinct so `instanceof`
 * discrimination tells "op missing" from "binding missing" from
 * "column missing" from "view missing".
 *
 * `Error.prototype.name` is deliberately left as the subclass identifier
 * (set via `new.target.name`), NOT overridden with the typed token —
 * Sentry, DevTools, `RootErrorBoundary`, and `err.toString()` all read
 * `Error#name` as the error *type*, and grouping by typed token instead
 * of class identifier scattered every distinct user typo into its own
 * issue group. The typed token lives on `this.typed` and is what
 * `suggestions()` fuzzy-matches against `available`.
 */
export abstract class UnknownNameError extends EvaluationError {
  readonly #emptyHint: string;
  readonly #listPrefix: string;
  constructor(
    readonly typed: string,
    readonly available: readonly string[],
    pos: SourcePos,
    message: string,
    emptyHint: string,
    listPrefix: string,
  ) {
    super(message, pos);
    // Restore the class identifier on Error#name — base `Error`
    // constructor sets it to `'Error'`, and we want subclass names to
    // flow through to Sentry / DevTools / RootErrorBoundary consumers.
    this.name = new.target.name;
    this.#emptyHint = emptyHint;
    this.#listPrefix = listPrefix;
  }
  override suggestions(): readonly string[] {
    return availableNamesSuggestion(this.typed, this.#emptyHint, this.#listPrefix, this.available);
  }
}

// Array-accessor-ish names agents reach for that are NOT ops — there is no positional
// element accessor. Steer them to projection / row fan-out / reduction instead.
const ARRAY_ACCESSOR_OP_NAMES: ReadonlySet<string> = new Set(['get', 'at', 'item', 'index', 'element']);

const ARRAY_ACCESSOR_HINT =
  'There is no positional array accessor op. To read a field off a relation list, project across it with ' +
  '`(col % <rel> <field>)` (returns an array); `unroll` to fan out one row per element; `aggregate` to reduce ' +
  '(`(sum :v)` / `(first :v)`). For arrays specifically, `first` / `last` / `nth` / `str-join` now exist.';

export class UnknownOpError extends UnknownNameError {
  readonly #hint?: string;
  constructor(name: string, available: readonly string[], pos: SourcePos, hint?: string) {
    super(name, available, pos, `unknown op: ${name}`, 'No ops are registered.', 'Available ops');
    // Explicit hint wins; otherwise synthesize the accessor hint for the classic misses.
    this.#hint = hint ?? (ARRAY_ACCESSOR_OP_NAMES.has(name) ? ARRAY_ACCESSOR_HINT : undefined);
  }
  override suggestions(): readonly string[] {
    const base = super.suggestions();
    return this.#hint === undefined ? base : [this.#hint, ...base];
  }
}

export class UnboundSymbolError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unbound symbol: ${name}`,
      'No bindings are in scope. Introduce one via (def name expr).',
      'Available names in scope',
    );
  }
}

/**
 * Map a Zod issue path to a human-facing argument label for `ShapeError`.
 * Normalised op calls have shape `{ name, params: [...], <kwarg>: ... }`,
 * so `path[0]` is `'params'` for a positional (label `#<index>`), `'name'`
 * for the op-name literal (no arg label — the issue is about the call
 * itself), and any other key for a keyword argument (label `:<key>`).
 * Single source of truth for the issue-path → arg-label mapping, shared by
 * every site that turns a Zod issue into a `ShapeError`.
 */
export const argRefFromZodPath = (path: readonly PropertyKey[]): string | undefined => {
  const head = path[0];
  if (head === undefined || head === 'name') {
    return undefined;
  }
  if (head === 'params') {
    return `#${String(path[1] ?? 0)}`;
  }
  return `:${String(head)}`;
};
/**
 * The raw Zod-v4 issue fields {@link engineZodErrorMap} reads. Zod's
 * internal `$ZodRawIssue` is a `code`-discriminated union; this is the
 * narrow structural view the formatter needs — a deliberate boundary
 * refinement (a Zod issue object is untyped input from our perspective).
 */
type ZodRawIssueView = {
  readonly code: string;
  readonly path?: readonly PropertyKey[];
  readonly origin?: string;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly inclusive?: boolean;
  readonly input?: unknown;
  readonly keys?: readonly string[];
  readonly errors?: readonly (readonly { readonly expected?: string; readonly values?: readonly unknown[] }[])[];
};

/**
 * Engine-wide Zod error map — the SSOT that rewrites Zod's opaque default
 * issue text ("Too small: expected array to have >=2 items", "Invalid
 * input") into clear, s-expr-oriented messages naming the arity / shape
 * contract the caller actually violated. Installed *parse-level* at every
 * op-call validation via {@link parseOpCall}; Zod-v4 precedence keeps a
 * schema-level `{ error }` (e.g. `col`'s domain hint) winning over this
 * map, so per-op messages stay authoritative. Returns `undefined` for
 * codes whose default is already clear (`invalid_type`, enum / literal
 * `invalid_value`) — Zod then keeps that default; this map only rewrites
 * the genuinely confusing ones (array / string arity, bare `invalid_union`,
 * unrecognized keyword args).
 */
export const engineZodErrorMap: z.core.$ZodErrorMap = (issue) => {
  const i = issue as unknown as ZodRawIssueView;
  const path = i.path ?? [];
  // A failure on the call's positional list itself reads as "argument";
  // anything deeper (a vector element, a kwarg vector) reads as "element".
  const noun = path[0] === 'params' && path.length === 1 ? 'argument' : 'element';
  const count = (n: number, word: string): string => `${word}${n === 1 ? '' : 's'}`;
  const gotArray = Array.isArray(i.input) ? `, got ${i.input.length}` : '';
  // `.min()` / `.max()` ⇒ inclusive bound; `.gt()` / `.lt()` ⇒ exclusive.
  const isInclusive = i.inclusive !== false;

  if (i.code === 'too_small') {
    const min = Number(i.minimum ?? 0);
    if (i.origin === 'array') {
      return `expected ${isInclusive ? `at least ${min}` : `more than ${min}`} ${count(min, noun)}${gotArray}`;
    }
    if (i.origin === 'string') {
      return min <= 1 ? 'must not be empty' : `must be at least ${min} characters`;
    }
    return undefined;
  }

  if (i.code === 'too_big') {
    const max = Number(i.maximum ?? 0);
    if (i.origin === 'array') {
      return `expected ${isInclusive ? `at most ${max}` : `fewer than ${max}`} ${count(max, noun)}${gotArray}`;
    }
    if (i.origin === 'string') {
      return `must be at most ${max} characters`;
    }
    return undefined;
  }

  if (i.code === 'unrecognized_keys') {
    const keys = i.keys ?? [];
    return `unknown keyword ${count(keys.length, 'argument')}: ${keys.map((k) => `:${k}`).join(', ')}`;
  }

  if (i.code === 'invalid_union') {
    const options = [
      ...new Set(
        (i.errors ?? []).flat().flatMap((leaf): readonly string[] => {
          if (Array.isArray(leaf.values)) {
            return leaf.values.map((v) => JSON.stringify(v));
          }
          if (leaf.expected !== undefined) {
            return [leaf.expected];
          }
          return [];
        }),
      ),
    ];
    return options.length > 0 ? `expected one of: ${options.join(' | ')}` : undefined;
  }

  return undefined;
};

/**
 * Keyword-node accessor — returns the name when `node` discriminates as
 * a keyword via the engine's `astShape` SSOT, `undefined` otherwise.
 */
const kwName = (node: unknown): string | undefined => {
  const shape = astShape(node);
  return shape.kind === 'keyword' ? shape.name : undefined;
};

/**
 * Build a kwarg-name acceptor for an op schema's top-level `z.object`
 * shape — the SSOT for "is this `:keyword` liftable as a kwarg on this
 * op?". Returns a predicate over the keyword name:
 *
 *   - Declared shape keys (every key other than `name` / `params`)
 *     always lift (the typical op with named kwargs like `:on`, `:type`).
 *   - When the schema declares a `.catchall(<codec>)`, ANY keyword name
 *     (still excluding `name` / `params`) lifts — that's how
 *     user-named arbitrary kwargs flow (e.g. derive's `:col-name`
 *     kwarg form, where the column name is user-chosen and cannot be
 *     pre-declared on the object shape).
 *   - Non-object schemas (wrapped, unions, …) return a no-op
 *     predicate; the lift then has no effect.
 */
const kwargAcceptor = (schema: z.ZodType): ((name: string) => boolean) => {
  const def = zdef(schema);
  if (def === undefined) {
    return () => false;
  }
  const declared = new Set(Object.keys(def.shape ?? {}).filter((k) => k !== 'name' && k !== 'params'));
  if (def.catchall === undefined) {
    return (n) => declared.has(n);
  }
  return (n) => n !== 'name' && n !== 'params';
};

/**
 * Schema-directed kwarg lift. Promotes `:k v` pairs from `data.params`
 * into top-level kwarg fields on `data` based on the op schema's
 * acceptor (see {@link kwargAcceptor}). The sweep is positional-
 * agnostic — pairs may appear at the head, interleaved with positional
 * children, or at the tail; each accepted keyword followed by a value
 * is lifted regardless of where it sits in the call. Catchall-typed
 * schemas (`.catchall(<codec>)`) lift any keyword — that's how
 * user-named arbitrary kwargs flow (e.g. derive's `:col-name` form).
 *
 * First-occurrence wins on a repeated kwarg; later occurrences stay
 * positional, so Zod / the op's apply surfaces the conflict on the
 * stranded keyword instead of silently overwriting.
 *
 * A trailing orphan keyword (declared keyword with no following value)
 * stays positional — the lift only fires when `params[i+1]` is in
 * range.
 *
 * Returns the input unchanged when `data` isn't an op-call-shaped
 * object or no pair lifts.
 */
export const liftKwargs = (schema: z.ZodType, data: unknown): unknown => {
  if (typeof data !== 'object' || data === null) {
    return data;
  }
  const obj = data as { readonly params?: unknown };
  if (!Array.isArray(obj.params)) {
    return data;
  }
  const accepts = kwargAcceptor(schema);
  const params = obj.params;
  // Sweep left-to-right. At each index, either lift the `:k v` pair
  // (accepted, not-yet-lifted, value-in-range) and skip the value slot,
  // or keep the node as positional. The accumulator is mutated in
  // place — kept/kwargs grow via push/assign instead of spreads to keep
  // the fold O(n) rather than O(n²).
  const fold = params.reduce<{
    kept: unknown[];
    kwargs: Record<string, unknown>;
    skipNext: boolean;
  }>(
    (acc, node, i) => {
      if (acc.skipNext) {
        acc.skipNext = false;
        return acc;
      }
      const name = kwName(node);
      if (name === undefined || !accepts(name) || Object.hasOwn(acc.kwargs, name) || i + 1 >= params.length) {
        acc.kept.push(node);
        return acc;
      }
      acc.kwargs[name] = params[i + 1];
      acc.skipNext = true;
      return acc;
    },
    { kept: [], kwargs: {}, skipNext: false },
  );
  if (Object.keys(fold.kwargs).length === 0) {
    return data;
  }
  return { ...obj, params: fold.kept, ...fold.kwargs };
};

/**
 * Validate a normalised op-call shape against its registered Zod schema
 * with {@link engineZodErrorMap} installed. SSOT for op-call validation:
 * every dispatch site (eager + special op calls in `evaluator.ts`,
 * dashboard tile / sub-form dispatch in `ops/dashboard/dispatch.ts`)
 * routes through here, so the clearer messages can never be forgotten at
 * a new call site.
 *
 * {@link relaxSymbols} widens string/enum leaves so they accept a bare
 * s-expr symbol in addition to the quoted form. Dispatchers are
 * responsible for applying {@link liftKwargs} on the raw AST call
 * before passing it through — the lift operates on `{$kw}` AST nodes
 * which the eager path's pre-evaluation would otherwise turn into
 * opaque `Keyword` Values.
 */
export const parseOpCall = <SchemaType extends z.ZodType>(schema: SchemaType, data: unknown) =>
  (relaxSymbols(schema) as SchemaType).safeParse(data, { error: engineZodErrorMap });
/**
 * Translate a {@link parseOpCall} failure into the engine's leaf error
 * shape. SSOT for "Zod issues → ExprError list" — used by every
 * dispatch site (`evaluator.ts`'s eager + special paths, dashboard
 * `dispatch.ts`, `compileAggPair`). Each issue becomes a
 * {@link ShapeError} pinned to the sub-path's sourcePos via
 * {@link argRefFromZodPath} for human-facing argument labels.
 */
export const zodIssuesToErrors = (err: z.ZodError, basePath: string, spans: Spans): readonly ExprError[] =>
  err.issues.map((issue) => {
    const fullPath = joinPath(basePath, issue.path.map(String));
    const pos = spans.get(fullPath) ?? opCallPos(spans, basePath);
    return new ShapeError(issue.message, pos, argRefFromZodPath(issue.path));
  });

/**
 * Throw the collected `errors` as either the single leaf (when one) or
 * an {@link AggregateExprError} (when many). Centralises the throw shape
 * so callers don't repeat the length check. Precondition:
 * `errors.length > 0`.
 */
export const throwErrors = (errors: readonly ExprError[]): never => {
  if (errors.length === 1) {
    const [first] = errors;
    if (first === undefined) {
      throw new Error('throwErrors: empty errors list');
    }
    throw first;
  }
  throw new AggregateExprError(errors);
};

/**
 * A schema validation failure during op-call dispatch. Wraps one Zod
 * issue at a time — multiple issues from a single Zod parse become
 * multiple `ShapeError` instances, aggregated via `AggregateExprError`.
 *
 * `argRef` names the offending argument when the issue is attributable to
 * one (`":span"` for a keyword arg, `"#0"` for a positional); `undefined`
 * means the issue concerns the call as a whole. When set, the rendered
 * message is prefixed with `argument <argRef>: ` and `suggestions()`
 * returns a recovery hint naming that argument — the guidance an LLM
 * agent needs to locate and fix the offending arg.
 */
export class ShapeError extends EvaluationError {
  constructor(
    readonly issueMessage: string,
    pos: SourcePos,
    readonly argRef?: string,
  ) {
    super(argRef === undefined ? issueMessage : `argument ${argRef}: ${issueMessage}`, pos);
  }

  override suggestions(): readonly string[] {
    if (this.argRef === undefined) {
      return [];
    }
    return this.argRef.startsWith(':')
      ? [`Check the value passed to the ${this.argRef} keyword argument against the op's signature.`]
      : [`Check positional argument ${this.argRef} against the op's signature.`];
  }
}
/**
 * Thrown when a `(sort …)` key bracket is malformed — the agent fused
 * several columns into one bracket (`[:a :b]`, where `:b` is read as a
 * direction) or overran the 1-column-plus-optional-direction shape
 * (`[:a :b :asc]`). Each sort column needs its own bracket. `fix` is the
 * corrected expression reconstructed from the offending columns (or
 * `null` when no column keyword was recoverable), surfaced via
 * `suggestions()` so the agent self-corrects with a concrete example
 * built from its own input. SSOT for the sort-key recovery wording.
 */
export class SortKeyError extends ShapeError {
  constructor(
    issueMessage: string,
    pos: SourcePos,
    readonly fix: string | null,
  ) {
    super(issueMessage, pos);
  }

  override suggestions(): readonly string[] {
    if (this.fix !== null) {
      return [
        `Each sort column needs its own bracket. Rewrite as \`${this.fix}\` — a bracket holds exactly one column plus an optional trailing \`:asc\` / \`:desc\`, so sort by several columns by listing them as separate brackets.`,
      ];
    }
    return [
      'Give each sort column its own bracket: `(sort [:a] [:b :desc])` orders by `a` ascending then `b` descending. A bracket holds one column and an optional `:asc` / `:desc`, never a second column.',
    ];
  }
}
/**
 * Thrown by `classifyPerCell` when a single per-cell-dispatching op
 * (`time-bucket`, a `window` op) lists a mix of TS-ref (`nested-dataset`)
 * and plain columns. The two forms have different output cardinalities —
 * per-cell preserves outer rows and nests buckets per cell; the plain form
 * collapses rows into `(bucket × group-by)` groups — so they cannot share
 * one call. `tsRefCols` / `plainCols` are the offending columns partitioned
 * by kind; `suggestions()` names each partition and the split recipe so the
 * caller can see the kind divergence the auto-dispatch syntax hides. SSOT
 * for the mix-rejection wording.
 */
export class MixedColumnKindError extends ShapeError {
  constructor(
    readonly opLabel: string,
    readonly plainLabel: string,
    readonly tsRefCols: readonly string[],
    readonly plainCols: readonly string[],
    pos: SourcePos,
  ) {
    super(
      `${opLabel} cannot mix nested-dataset (TS-ref) and ${plainLabel} columns in one call; split into separate calls`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    const tsRefList = this.tsRefCols.map((c) => `:${c}`).join(' ');
    const plainList = this.plainCols.map((c) => `:${c}`).join(' ');
    return [
      `\`${this.opLabel}\` picks its form from column kind: TS-ref (nested-dataset) columns [${tsRefList}] bucket per-cell (outer rows preserved, buckets nested per cell), while ${this.plainLabel} columns [${plainList}] bucket at the row level (rows collapse into buckets). The two produce different row cardinalities, so they cannot share one call — split into two: one \`${this.opLabel}\` listing only [${tsRefList}], and one listing only [${plainList}].`,
    ];
  }
}

/**
 * Thrown by the threading macros (`->` / `->>`) when a dataset SOURCE op
 * (`query` / `datapoints`) appears as a non-seed step. A source op opens a
 * fresh pipeline — its first positional arg is the view / external-id it
 * reads, never an upstream dataset — so threading the accumulator into it
 * injects a dataset where a string is expected, which Zod would otherwise
 * reject downstream with an opaque `argument #0: expected one of: string`.
 * This leaf takes over at desugar time so the agent learns the real mistake
 * (a source op cannot be chained) instead of a position-shifted type error.
 */
export class SourceOpInPipelineError extends EvaluationError {
  constructor(
    readonly opName: string,
    pos: SourcePos,
  ) {
    super(
      `\`${opName}\` is a source op that *begins* a pipeline — it cannot be chained as a \`->\` / \`->>\` step (doing so feeds the upstream dataset into its view-name argument). Run it as its own call and reference the bound result by name, or make it the seed of the pipeline.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      `Issue \`${this.opName}\` as a separate call, bind its output, then reference that dataset by name in the downstream pipeline.`,
      `Or make \`${this.opName}\` the seed (first form) of the thread, e.g. \`(-> (${this.opName} …) (filter …) (aggregate …))\`.`,
    ];
  }
}

/**
 * Explicit type-mismatch from an op's apply function (e.g. `(dataset)`
 * rejecting a non-Dataset RHS). Distinct from `ShapeError` (which wraps
 * pre-apply schema validation) and `OpExecutionError` (which wraps
 * unexpected throws from apply).
 */
export class TypeMismatchError extends EvaluationError {
  protected override readonly staticHint?: string;
  constructor(
    readonly actualKind: string,
    readonly expectedKind: string,
    readonly context: string,
    pos: SourcePos,
    hint?: string,
  ) {
    // `actualKind === 'unknown'` is the self-correction-hostile case:
    // it almost always means the referenced binding/dataset hasn't been
    // produced yet (so its value never resolved to a Dataset), NOT that
    // the agent passed a literally wrong type. Spell that out + give a
    // concrete recovery instead of the opaque "got unknown". `context`
    // already embeds the offending binding/column (e.g. `(col wells x)`
    // / `(join ds-a ds-b :on […])`), so the message names it without
    // any new constructor parameter.
    const unresolved = actualKind === 'unknown' && /Dataset/i.test(expectedKind);
    super(
      unresolved
        ? `${context} requires ${expectedKind}, but the referenced dataset is unresolved (its value never resolved to a Dataset). The producing tool likely hasn't run yet — run the tool that creates this dataset (and bind it) before referencing it here.`
        : `${context} requires ${expectedKind}; got ${actualKind}`,
      pos,
    );
    this.staticHint = unresolved
      ? 'A `got unknown` here means the binding exists in name only — its dataset value was never produced. Run the producing tool first, then reference it.'
      : hint;
  }
}
/**
 * Thrown by the `=` / `!=` / `in` comparison operators when exactly one
 * operand is a CDF instance reference (a direct-relation `{space,
 * externalId}` struct) and the other is a scalar. An instance-reference
 * column (`wellString`, `well`, `string`, `field`, …) compares either to
 * another instance reference or — far more commonly — through its scalar
 * `externalId` sub-path; comparing the whole struct to a bare
 * `externalId`/string can never match, so instead of silently filtering
 * to zero rows we fail loudly with the two recovery shapes.
 *
 * Detected at the value-equality SSOT (`dateAwareEquals`), so it covers
 * the CDF view provider's residual filter predicates and transform-level
 * `(filter …)` uniformly, in both operand orders.
 */
export class DirectRelationScalarComparisonError extends EvaluationError {
  constructor(
    readonly scalarValue: string,
    pos: SourcePos,
  ) {
    super(
      `cannot compare an instance reference (a direct-relation \`{space, externalId}\` struct) to the scalar value ${scalarValue}: an instance-reference column matches another instance reference or its \`externalId\` sub-path, never a bare scalar`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'Drill the instance reference to its scalar `externalId` sub-path so both sides compare `externalId` strings — e.g. `(in (col wellString externalId) (col ent externalId))`.',
      'Or compare against full instance references `{space, externalId}` — e.g. scope through FlatHierarchy where the right-hand column is itself an instance reference: `(in (:wellString %) (col fh string))`.',
    ];
  }
}

/**
 * Thrown when expression-context code (a `filter` predicate, a `derive`
 * right-hand expression, etc.) walks into a column cell that is itself
 * a `Dataset`. Cells may be `Scalar | Dataset`, but the scalar-only
 * verbs `filter` / `derive` have no per-cell dispatch, so references
 * into Dataset-cell columns surface as this error so the failure mode is
 * explicit instead of producing nonsensical results inside arquero's
 * row functions.
 *
 * Carries the offending column name (`column`) and the source position
 * of the column reference inside the expression.
 */
export class NestedDatasetCellError extends EvaluationError {
  constructor(
    readonly column: string,
    pos: SourcePos,
  ) {
    super(
      `column \`${column}\` holds a Dataset cell; expression-context verbs (filter, derive) do not yet support per-cell dispatch on nested Datasets`,
      pos,
    );
  }
}

export class RebindingError extends EvaluationError {
  protected override readonly staticHint = 'Rename the binding or omit the duplicate declaration.';
  constructor(
    override readonly name: string,
    pos: SourcePos,
  ) {
    super(`cannot rebind '${name}': name is already bound`, pos);
  }
}
export class ShadowingOpError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    override readonly name: string,
    pos: SourcePos,
  ) {
    super(`cannot bind '${name}': name is a registered op`, pos);
    this.staticHint = `Rename the binding; the op '${name}' would be shadowed.`;
  }
}

export class EmptyScriptError extends EvaluationError {
  constructor() {
    super('empty script — no forms to evaluate', NO_POS);
  }
}
/**
 * Thrown when a {@link LambdaValue} is invoked with the wrong number of
 * arguments. Surfaces at the call site (the `(myLambda …)` expression
 * the caller wrote), so the LLM sees "you passed 3 args, the lambda
 * takes 2" pointing at their call rather than the lambda definition.
 */
export class LambdaCallArityError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly expected: number,
    readonly received: number,
    pos: SourcePos,
  ) {
    super(`lambda expects ${expected} argument(s); received ${received}`, pos);
    this.staticHint =
      received < expected
        ? `Add ${expected - received} more argument(s) to the call.`
        : `Remove ${received - expected} argument(s) from the call.`;
  }
}
/**
 * Thrown when a verb-invoked projector lambda (filter predicate, derive
 * projector, sort computed key, ...) is supplied with the wrong number
 * of parameters. Surfaces at the lambda DEFINITION (so the LLM sees
 * "your `#(…)` body has no `%` reference, so it desugared to a zero-arg
 * lambda; the verb wants a 1-param row projector") rather than at the
 * verb's internal call.
 *
 * Distinct from {@link LambdaCallArityError}, which fires when the
 * USER writes a call like `(my-lambda 1 2)` with the wrong number of
 * args. `ProjectorArityError` fires when a verb-supplied lambda's
 * parameter list is the wrong shape for the row projection the verb is
 * about to perform.
 */
export class ProjectorArityError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly expected: number,
    readonly received: number,
    pos: SourcePos,
  ) {
    super(`projector lambda takes ${expected} parameter(s); got ${received}`, pos);
    this.staticHint =
      received < expected
        ? `Define your lambda as \`(fn [row] …)\` or \`(fn [_] …)\` if the body doesn't read the row. \`#(…)\` only auto-binds \`%\` when the body references it.`
        : `Remove ${received - expected} parameter(s) from the lambda definition.`;
  }
}

/**
 * An op's apply function threw an unexpected (non-`ExprError`) error.
 * The original message is preserved verbatim; callers that need the
 * original throwable can read `underlyingMessage`.
 */
export class OpExecutionError extends EvaluationError {
  constructor(
    override readonly name: string,
    readonly underlyingMessage: string,
    pos: SourcePos,
  ) {
    super(`op '${name}' threw: ${underlyingMessage}`, pos);
  }
}
/**
 * A column-reference in an expression or column-list context named a
 * column not present on the surrounding dataset's `Schema`. Surfaced
 * by the pre-evaluation type-check pass so the LLM gets a
 * spell-check diagnostic without paying for any I/O.
 *
 * Distinct from `UnboundSymbolError` (which is for env-level dataset
 * bindings) so callers can tell "this name isn't a dataset I can bind
 * to" from "this name isn't a column on the dataset I'm transforming."
 */
export class UnknownColumnRefError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unknown column: ${name}`,
      `Column \`${name}\` is not on the upstream dataset's schema. Pick one of the listed available columns, or produce \`${name}\` upstream (via select/derive/rename) before referencing it here.`,
      'Available columns on the upstream dataset',
    );
  }
}
/**
 * A `(col …)` sub-path walked into a field that isn't on the walkable
 * surface — a record/struct cell key at runtime, or a declared subfield
 * at typecheck. Distinct from {@link UnknownColumnRefError}: the miss is
 * on a FIELD of a column's cells, not on the dataset's schema, so
 * "produce it upstream via select/derive/rename" is the wrong remedy —
 * the fix is picking one of the fields the cell actually carries.
 */
export class UnknownFieldRefError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unknown field: ${name}`,
      `\`${name}\` is not a field on this column's cells — a sub-path like (col ds relation ${name}) walks the cell's own fields, so deriving \`${name}\` upstream will not help. Pick one of the listed fields instead.`,
      'Fields available on the cells of this column',
    );
  }
}

/**
 * `compileExpr` was invoked on an op call whose schema has no `compileExpr`
 * method. Surfaces when a non-expression op (e.g. `def`, `aggregate`)
 * appears inside an expression-context argument, like the predicate of a
 * `filter` or the body of a `derive`.
 */
export class NotAnExpressionOpError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    override readonly name: string,
    pos: SourcePos,
  ) {
    super(`op '${name}' cannot be used in an expression context`, pos);
    this.staticHint = `'${name}' has no compileExpr method. Expression-context arguments (filter predicates, derive bodies, sort keys) accept scalar/comparison/boolean ops + column references, not top-level forms.`;
  }
}
/**
 * Static recovery hint per {@link MaterializationError} reason — the SSOT
 * that pairs each failure mode with its "how to fix" guidance, keyed so
 * the constructor reads one map entry instead of a growing ternary chain.
 */
const MATERIALIZATION_HINTS: Record<MaterializationError['reason'], string> = {
  'missing-provider':
    'Wire a provider registry onto the `EvalContext` (its `getProviderById`) so the materialiser can resolve a provider-backed source to its `fetch`.',
  'schema-mismatch':
    "A verb's `propagateSchema` disagrees with its `apply`'s output. Compare the produced columns against the verb's propagation and fix the discrepancy.",
};

/**
 * Thrown by the materializer when something prevents
 * the lazy-plan execution from completing. Two concrete failure modes,
 * distinguished by `reason`:
 *
 *   - **`missing-provider`** — a `ProviderSource` plan references a
 *     `providerId` no provider in the registry on `EvalContext` resolves
 *     (the registry was not wired, or the source was minted against a
 *     provider absent from this context).
 *   - **`schema-mismatch`** — after running the verb chain, the actual
 *     output schema disagrees with the `LazyDataset`'s declared schema.
 *     Indicates a `propagateSchema` bug in one of the verbs; caught at
 *     the materialisation boundary instead of letting downstream
 *     consumers see surprising shapes.
 *
 * The `reason` discriminator is a string tag (not a separate class
 * hierarchy) — both failures share the same shape (message + pos) and
 * the same recovery path (fix the offending verb/config). Tests assert
 * via `instanceof MaterializationError` and inspect `reason` when they
 * care which case fired.
 */
export class MaterializationError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly reason: 'missing-provider' | 'schema-mismatch',
    message: string,
    pos: SourcePos,
  ) {
    super(message, pos);
    this.staticHint = MATERIALIZATION_HINTS[reason];
  }
}

/**
 * Raised when a requested view's `externalId` is not in the host-supplied
 * view set. Extends `UnknownNameError` so callers get the standard
 * "available names + did-you-mean" diagnostic shape. The generic `query`
 * source op raises the broader `UnknownDatasetError` (no provider claims
 * the name) in its place; this class remains for non-provider view-name
 * lookups.
 *
 * Distinct from `UnknownOpError` (op-name lookup), `UnboundSymbolError`
 * (env/session lookup), and `UnknownColumnRefError` (column lookup) so
 * call sites can tell the four "name not in scope" failures apart.
 */
export class UnknownViewError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unknown view: ${name}`,
      'No views are available — register a provider that owns this view so the engine can resolve its metadata.',
      'Available view externalIds',
    );
  }
}
/**
 * Thrown by a dataset source op (`query` / `datapoints`) when no provider
 * in the op's routing group `claims(name)` — the provider-framework
 * counterpart of `UnknownViewError`. Extends `UnknownNameError` so
 * callers get the standard "available names + did-you-mean" diagnostic
 * shape; `available` is the set of dataset names claimed by providers in
 * the group.
 *
 * Distinct from `UnknownViewError` so a name unknown to the *provider
 * registry* (no provider claims it) reads differently from a CDF view
 * name absent from the registered view set.
 */
export class UnknownDatasetError extends UnknownNameError {
  constructor(name: string, available: readonly string[], pos: SourcePos) {
    super(
      name,
      available,
      pos,
      `unknown dataset: ${name}`,
      'No dataset providers are registered — wire a provider registry onto the `EvalContext` so the engine can resolve dataset names.',
      'Available dataset names',
    );
  }
}

/**
 * Thrown by the CDF view provider (behind the generic `query` op) when
 * the requested limit exceeds the engine's hard cap. The cap is a
 * guardrail against accidental unbounded fetches; surface it as a
 * structured error rather than a silent clamp so the LLM can react.
 *
 * Carries the requested value AND the cap so the caller knows the
 * legal range; not a discriminator-style error (the failure has a
 * single shape).
 */
export class LimitTooLargeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly limit: number,
    readonly max: number,
    pos: SourcePos,
  ) {
    super(`:limit ${limit} exceeds the engine cap of ${max}`, pos);
    this.staticHint = `Pass \`:limit\` ≤ ${max}, or page the query — the engine does not silently clamp oversized limits.`;
  }
}
/**
 * Thrown when a knowledge-graph read pages all the way to the engine cap
 * (`KG_QUERY_MAX_LIMIT`) with MORE rows still available on CDF's cursor. The
 * engine pages through CDF's per-call instance-query limit to return every
 * match; a read that overruns the cap is effectively unbounded (typically a
 * view with no server-side filter), so it fails loud — never silently
 * truncates — and points the caller at narrowing the query. An explicit,
 * smaller `:limit` is an intended cap and stops quietly instead.
 */
export class QueryResultTooLargeError extends EvaluationError {
  protected override readonly staticHint =
    'Add a server-side filter (an identity `=` / `in` on space / externalId, or a field / well scope) so the read ' +
    'returns fewer than the cap, or pass an explicit smaller `:limit` if a partial result is acceptable.';
  constructor(
    readonly cap: number,
    pos: SourcePos,
  ) {
    super(`knowledge-graph read exceeded the engine cap of ${cap} rows with more matches still available`, pos);
  }
}

/**
 * Thrown by the shared set-reference resolver when a `:filter` predicate
 * resolves a `(col other-ds idCol)` cross-dataset reference whose
 * materialised value set exceeds the CDF-safe cap (`SET_REF_MAX`). The
 * values would be pushed to CDF as an `in` filter; CDF rejects oversized
 * lists, so fail fast with an actionable hint rather than surfacing an
 * opaque server rejection seconds later.
 *
 * Carries the resolved set size AND the cap so the caller knows by how
 * much to narrow the upstream query; single-shape failure (not a
 * discriminator-style error).
 */
export class SetRefTooLargeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly count: number,
    readonly max: number,
    pos: SourcePos,
  ) {
    super(`cross-dataset \`in\`-set resolved to ${count} values, exceeding the cap of ${max}`, pos);
    this.staticHint = `Narrow the upstream query so the referenced column yields ≤ ${max} distinct values (e.g. add a tighter \`:filter\` or \`:limit\` to the dataset the \`(col …)\` points at).`;
  }
}

/**
 * Thrown by the shared set-reference resolver when a `(col other-ds …)`
 * cross-dataset reference inside a `:filter` predicate resolves to ZERO
 * distinct values. A pushed-down `in` filter with an empty value list
 * silently matches nothing, which almost always reflects an upstream
 * query that returned no rows (a mistake the LLM should see and correct)
 * rather than an intentional "match nothing" — surface it as a hard,
 * actionable error instead of an empty result set.
 */
export class EmptySetRefError extends EvaluationError {
  protected override readonly staticHint =
    'The referenced dataset produced no rows, so the cross-dataset filter would match nothing. Check that the upstream query (the dataset the `(col …)` points at) actually returns data, or widen its `:filter` / `:limit`.';
  constructor(pos: SourcePos) {
    super('cross-dataset set reference resolved to an empty set (the referenced column yielded zero values)', pos);
  }
}

/**
 * Thrown when a dataset-valued sub-query nested inside a `:filter`
 * predicate references the OUTER query's row alias (`%`). Nested
 * sub-queries are resolved exactly ONCE and inlined as a literal set
 * before the outer query runs, so they cannot depend on a per-row value
 * of the outer query — a correlated reference would require a per-row
 * re-evaluation (an N+1 the resolve-once model deliberately forbids).
 *
 * Carries the offending row alias so the LLM sees which token made the
 * sub-query correlated; single-shape failure (not a discriminator error).
 */
export class CorrelatedSubqueryError extends EvaluationError {
  protected override readonly staticHint =
    'A nested sub-query is resolved once and shared across all rows, so it cannot reference the outer row (`%`). Rewrite it as an independent query whose result is compared against the outer column, e.g. `(= (:fieldInstance %) (query "Field" (filter #(= (:name %) "Field A"))))`.';
  constructor(
    readonly alias: string,
    pos: SourcePos,
  ) {
    super(
      `a nested sub-query references the outer row alias \`${alias}\`; correlated sub-queries are not supported`,
      pos,
    );
  }
}

/**
 * Thrown when a dataset-valued expression appears in a comparison
 * position but the engine cannot reduce it to a single literal column.
 * A provider may supply a value-collapse rule deciding which column to
 * extract; absent that, the generic fallback collapses only an explicit
 * single-column projection — `(col inner-query <name>)` — or an inner
 * result that already has exactly one column. A multi-column inner
 * dataset with no chosen column is ambiguous, so fail with an actionable
 * hint rather than guessing.
 *
 * Carries the inner dataset's column names so the caller knows which
 * column to project; single-shape failure (not a discriminator error).
 */
export class UncollapsibleRefError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly columns: readonly string[],
    pos: SourcePos,
  ) {
    super(
      `a dataset used in a comparison must reduce to one column, but the inner result has ${columns.length} columns (${columns.join(', ') || '(none)'})`,
      pos,
    );
    this.staticHint =
      'Project the inner query to the single column you mean to compare against — wrap it as `(col <inner-query> <columnName>)` — so the engine knows which values to inline.';
  }
}
/**
 * Thrown when a foreign `(col other-ds <struct-col>)` set-reference cannot be
 * inlined as a comparison set. A direct-relation membership now recovers the
 * full `{space, externalId}` identity from the source dataset's typed identity
 * axis, so this fires only when no such identity or scalar projection exists:
 * the foreign column is a `timeseries` / `nested-dataset` cell, or a struct
 * with no `externalId` field, or the foreign dataset carries no node identity
 * `{space, externalId}` at all (e.g. a derived / aggregated dataset that lost
 * it). The hint points the LLM at an instance-backed view that still carries
 * node identity, or a scalar subfield for plain struct comparisons.
 */
export class StructSetRefError extends EvaluationError {
  protected override readonly staticHint =
    'This set-reference has no node identity {space, externalId} and no scalar projection. ' +
    'For a relation/identity comparison, reference a column on an instance-backed view that still ' +
    'carries node identity — not a derived or aggregated dataset that has lost it. For a plain ' +
    'struct comparison, reference a scalar subfield of the struct (e.g. `(col other-ds someStruct field)`).';
  constructor(
    readonly detail: string,
    pos: SourcePos,
  ) {
    super(detail, pos);
  }
}

/**
 * Thrown when a facade provider's `rewrite` expands a query into an
 * expression whose result schema does not refine the schema the facade
 * declares for that dataset. A facade promises its callers a fixed output
 * shape (its `schema(name)`); the expansion it produces must satisfy that
 * promise so downstream column references stay valid. A mismatch is a
 * PROGRAMMING error in the facade definition — not an LLM mistake — so the
 * message names the facade and the offending columns to aid the author.
 */
export class FacadeSchemaError extends EvaluationError {
  protected override readonly staticHint =
    'This is a bug in the facade definition, not the query: its rewrite produced a result whose columns do not match the schema it declares. Align the expansion with the declared schema.';
  constructor(
    readonly datasetName: string,
    readonly declaredColumns: readonly string[],
    readonly expansionColumns: readonly string[],
    pos: SourcePos,
  ) {
    super(
      `facade '${datasetName}' expansion does not match its declared schema: declared [${declaredColumns.join(', ') || '(none)'}], expansion produced [${expansionColumns.join(', ') || '(none)'}]`,
      pos,
    );
  }
}
/**
 * Thrown by a `(datapoints …)` source query that selects no specific
 * series — an UNBOUNDED datapoints read. Datapoints are row-per-series
 * with a nested cell, so a query with no identity selection
 * (`externalId`/`space` `=`/`in` filter) would attempt to stream every
 * timeseries in the project. That is never the intent, so fail fast with
 * an actionable hint to add a bounding identity filter.
 */
export class UnboundedDatapointsError extends EvaluationError {
  protected override readonly staticHint =
    'Bound the query to specific series, e.g. `(datapoints (filter #(in (:externalId %) ["id-a" "id-b"])))` or `(filter #(= (:externalId %) "id-a"))`.';
  constructor(pos: SourcePos) {
    super(
      'a `(datapoints)` query must select specific series by `externalId`/`space`; an unbounded read is rejected',
      pos,
    );
  }
}

/**
 * Thrown when a per-cell aggregation / `time-bucket` is applied to a
 * nested-dataset cell that has no single value axis to reduce. A raw
 * timeseries cell carries one `value` column, so `(mean :value)` is
 * unambiguous; but a cell that was ALREADY bucketed into multiple
 * aggregate columns (e.g. `{bucket, value-min, value-max}`) has no single
 * `value` — re-aggregating it cannot pick which column to reduce. Carries
 * the cell's inner column names so the caller can collapse to one axis
 * first.
 */
export class AmbiguousLiftTargetError extends EvaluationError {
  protected override readonly staticHint =
    'Reduce the cell to a single value axis before bucketing again — apply one aggregate at a time, or project the cell to the column you want to re-aggregate.';
  constructor(
    readonly column: string,
    readonly innerColumns: readonly string[],
    pos: SourcePos,
  ) {
    super(
      `column '${column}' cells have no single value axis to aggregate (inner columns: ${innerColumns.join(', ') || '(none)'}); re-aggregating a multi-aggregate cell is ambiguous`,
      pos,
    );
  }
}

/**
 * Thrown by `(sort …)` when a positional arg names a column whose
 * `ColumnType` doesn't have a natural order — `struct`, `nested-dataset`,
 * `unknown`. Carries the offending column name + its rendered type so
 * the LLM can correct course (typically: pick a different column, or
 * project to a scalar via `derive` first).
 */
export class UnsortableColumnTypeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly columnType: string,
    pos: SourcePos,
    readonly scalarSubfields: readonly string[] = [],
  ) {
    const unresolvedType = columnType === 'unknown';
    super(
      unresolvedType
        ? `sort: column \`${column}\` has unresolved type \`unknown\` (its type was not inferred — typically an aggregate/join output), so it has no sortable order. Derive or time-bucket it to a scalar before sorting (e.g. \`(derive ds :${column}-key …)\` then sort on that).`
        : `sort: column \`${column}\` has type \`${columnType}\`, which has no natural order`,
      pos,
    );
    // Prefer a concrete sortable subfield when one exists (a struct/relation
    // exposes its scalar subfields); otherwise there is nothing on the column
    // to order by, so direct the LLM to a different scalar column. An
    // unresolved (`unknown`) type is a separate case: derive a scalar first.
    const recovery =
      scalarSubfields.length > 0
        ? relationColumnHint(column, scalarSubfields, 'sort')
        : pickDifferentColumnHint(column);
    this.staticHint = unresolvedType
      ? scalarProjectionHint(column)
      : `Sort only supports scalar column types (${SCALAR_HINT_KINDS.join(' / ')}). ${recovery}`;
  }
}

/**
 * Thrown by `(unnest …)` when a positional column name resolves to a
 * `ColumnType` other than `nested-dataset`. Mirrors
 * `UnsortableColumnTypeError`'s shape — the failure is a single
 * `(col, rendered-type)` pair, not a discriminator family.
 */
export class UnnestableColumnTypeError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly columnType: string,
    pos: SourcePos,
  ) {
    super(`unnest: column \`${column}\` has type \`${columnType}\`, which is not a nested-dataset column`, pos);
    this.staticHint =
      '`unnest` expands a nested cell (a timeseries ref or `dataset<…>`) into rows; a scalar ' +
      `column like \`${columnType}\` is already expanded — do not unnest it again. ` +
      'Canonical recipe — one timeseries column to a (day, value) line: `(unnest :oilRate :as [:day :oil])`. ' +
      `Several parallel rate columns at once: ${UNNEST_MULTI_COL_AS_HINT}. ` +
      'For a plain `array<…>` column (relation lists, string / number arrays) use `unroll` to fan out one row ' +
      'per element — NOT `unnest`, which is only for nested-dataset / timeseries cells.';
  }
}

/**
 * Thrown by `(unnest …)` when an inner column name collides with an
 * outer column name and the LLM has not supplied a `:as` rename for
 * the colliding inner column. Carries both names so the LLM can pick
 * a fresh alias.
 */
export class UnnestCollisionError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly collidingInnerName: string,
    pos: SourcePos,
  ) {
    super(
      `unnest: inner column \`${collidingInnerName}\` (from \`${column}\`) collides with an existing outer column`,
      pos,
    );
    this.staticHint =
      `The dataset already has a column \`${collidingInnerName}\`, so the inner name clashes. ` +
      `Choose a fresh inner name in \`:as\` (e.g. \`${collidingInnerName}_d\`). When unnesting multiple ` +
      `columns at once, ${UNNEST_MULTI_COL_AS_HINT}`;
  }
}
/**
 * Thrown by `(join …)` when an entry of the `:on` clause has an
 * unsupported AST shape — a side is neither a keyword (`:colname`) nor
 * a `(col …)` reference (the two SSOT forms for a join key). The
 * partner ShapeError emitted by Zod's `OnEntry` schema collapses every
 * failure mode to "Invalid input", so this leaf takes over: it names
 * the entry index, the side role (`outer` / `inner` / `self`), what
 * was actually received (e.g. `bare symbol \`well_id\``), and lists
 * the three valid `:on` shapes as suggestions. The most common LLM
 * mistake — passing a bare symbol where a keyword was meant — gets a
 * targeted "did you mean `:well_id`?" hint.
 */
export class JoinOnShapeError extends EvaluationError {
  constructor(
    readonly entryIndex: number,
    readonly role: 'outer' | 'inner' | 'self',
    readonly received: string,
    pos: SourcePos,
    readonly didYouMean?: string,
  ) {
    const roleLabel = role === 'self' ? '' : ` ${role} side`;
    const hint = didYouMean === undefined ? '' : ` — did you mean \`${didYouMean}\`?`;
    super(
      `:on entry #${entryIndex}${roleLabel}: expected a keyword (e.g. \`:colname\`) or a \`(col …)\` reference, got ${received}${hint}`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'Same-name self-join (one keyword, applied to both sides): `:on [:colname]`',
      'Different-name cross-join pair: `:on [[:outerCol :innerCol]]`',
      'Sub-path key (struct or relation column): `:on [(col ds rel externalId)]`',
    ];
  }
}

/**
 * Thrown at dashboard render time (4c.7) when a `(series …)` `:x`
 * column's leaf type is incompatible with the parent `(cartesian …)`'s
 * declared `:x-kind`. The mismatch can only be detected once the
 * referenced dataset is materialised (the V-shape carries an
 * unresolved `ColumnRef`), so this is a resolution-time leaf, not an
 * eval-time one. Mirrors `UnsortableColumnTypeError`'s `(col, type)`
 * shape, extended with the expected/actual pair + the x-kind context.
 */
export class SeriesXKindMismatchError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly seriesId: string,
    readonly xKind: string,
    readonly expectedLeafType: string,
    readonly actualLeafType: string,
    pos: SourcePos,
    recovery?: string,
  ) {
    super(
      `series \`${seriesId}\`: :x column resolves to \`${actualLeafType}\`, incompatible with cartesian :x-kind \`${xKind}\` (expected \`${expectedLeafType}\`)`,
      pos,
    );
    // `recovery` carries column-specific guidance from the caller; the
    // templated hint is the fallback.
    this.staticHint =
      recovery ??
      `Reference an :x column whose type matches :x-kind \`${xKind}\` (${expectedLeafType}), or change the cartesian's :x-kind.`;
  }
}

/**
 * Thrown at dashboard render time when a `(reference-line …)` /
 * `(reference-area …)` scalar attribute (`:value` / `:start` / `:end`)
 * has the wrong JS type for the axis it anchors to (e.g. a numeric
 * value on a `time` x-axis). Single failure shape; `xKind` is carried
 * when the offending axis is the cartesian's x-axis (it drives the
 * expected type) and omitted for the y-axis.
 */
export class RefValueTypeMismatchError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly attr: string,
    readonly axis: string,
    readonly expectedJsType: string,
    readonly actualJsType: string,
    pos: SourcePos,
    readonly xKind?: string,
  ) {
    super(
      `reference \`:${attr}\` on the \`${axis}\` axis is \`${actualJsType}\`, expected \`${expectedJsType}\`${
        xKind === undefined ? '' : ` for :x-kind \`${xKind}\``
      }`,
      pos,
    );
    this.staticHint = `Provide a \`${expectedJsType}\` for \`:${attr}\` on the \`${axis}\` axis.`;
  }
}

/**
 * Thrown at dashboard render time when a `ColumnRef`'s struct `path`
 * walks into a scalar (non-struct, non-nested) value mid-path. The
 * col op validates the path against the schema at construction, but a
 * lazy/materialised divergence (or a `unknown`-typed column that turns
 * out scalar) surfaces here. Mirrors `UnsortableColumnTypeError`'s
 * `(name, type)` pair shape plus the offending path segment.
 */
export class PathThroughScalarError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly column: string,
    readonly scalarType: string,
    readonly atSegment: string,
    pos: SourcePos,
  ) {
    super(
      `column \`${column}\`: struct path segment \`${atSegment}\` walks into a \`${scalarType}\` value, which is not a struct`,
      pos,
    );
    this.staticHint = `Drop the path segments after \`${atSegment}\`, or reference a struct / nested-dataset column instead.`;
  }
}
/**
 * Thrown at dashboard-eval time when a `(metric …)` `:value`
 * `ColumnRef` resolves to more than one row. A metric tile renders a
 * single scalar; a multi-row `:value` is a misconfigured ref (a raw
 * column instead of an aggregate). The mismatch can only be detected
 * once the referenced dataset is materialised (the V-shape carries an
 * unresolved `ColumnRef`), so this is a resolution-time leaf, not an
 * eval-time one. SSOT for the scalar-violation wording — both
 * `validateDashboardSpec` (execute-time, throws) and `MetricPanel`
 * (render-time, graceful card) read this class's `message`.
 */
export class MetricScalarError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly label: string,
    readonly rowCount: number,
    pos: SourcePos,
  ) {
    super(`metric \`${label}\`: :value resolved to ${rowCount} rows — expected a single scalar`, pos);
    this.staticHint =
      'Create a new dataset with 1-row `(aggregate …)` (e.g. `(count)`, `(mean …)`) so `:value` is a single number, or point `:value` at an already-scalar `(col <ds> <col>)`.';
  }
}
/**
 * Thrown at dashboard-eval time when a displayed table column, chart
 * series channel, or metric `:value` resolves to rows that are EVERY one
 * null. A column where no record carries a value is never a partial-data
 * situation the reader can act on — it is the signature of a ref that
 * never resolved: a direct-relation struct read for a `name` the view
 * does not hydrate, or an unresolved time-series handle. Partial nulls
 * are fine, and a 0-row resolve is permitted (a legitimately empty
 * result); only "has rows, all null" is the bug. Detectable only once
 * the referenced dataset is materialised, so this is a resolution-time
 * leaf. SSOT for the all-null wording — `validateDashboardSpec` throws
 * it at execute time so the agent re-queries before claiming success.
 */
export class AllNullColumnError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly subject: string,
    readonly rowCount: number,
    pos: SourcePos,
  ) {
    super(
      `${subject}: every one of ${rowCount} records is null — an entirely empty column/series signals the data was not queried or resolved correctly`,
      pos,
    );
    this.staticHint =
      'A column where every value is null usually points at a ref that never resolved: a direct-relation struct read for a `name` the view does not hydrate (source the enriched view, or carry the resolved struct through the pipeline and read `(col <ds> <struct> name)` at the leaf), or an unresolved time-series handle (resolve it before display). Verify the column carries data, or drop it from the tile.';
  }
}
/**
 * Thrown at dashboard-eval time when a `(cartesian …)` value channel — a
 * series `:y` or scatter `:z`, or a band `:lower` / `:upper` — resolves
 * to a non-numeric (string) value. Cartesian charts plot numbers on the
 * value axis; a string-typed value column contributes no plottable point
 * (every cell is dropped by the renderer's numeric narrowing), so the
 * chart comes up blank. The usual cause is a swapped binding: the
 * category names landed on the value channel and the measure on `:x`.
 * Detectable only once the referenced dataset is materialised, so this is
 * a resolution-time leaf. SSOT for the non-numeric-value wording —
 * `validateDashboardSpec` throws it at execute time so the agent reshapes
 * before claiming success.
 */
export class NonNumericValueError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly subject: string,
    readonly actualType: string,
    pos: SourcePos,
    recovery?: string,
  ) {
    super(
      `${subject}: resolves to \`${actualType}\` data, but a cartesian value channel must be numeric — a non-numeric value channel plots nothing and the chart renders blank`,
      pos,
    );
    // `recovery` carries channel-specific guidance computed by the caller
    // (which knows the bound columns and the sibling channel's type — e.g.
    // the concrete swapped-binding fix). The generic hint is the fallback.
    this.staticHint =
      recovery ??
      'A cartesian chart plots numbers on the value axis. `:x` is the category axis (labels, names, dates); `:y` is the plotted value, and `:z` (bubble size) and band `:lower`/`:upper` are values too — every value channel must be numeric. Reference a numeric column for the value channel, or drop the series.';
  }
}
/** Per-tile, per-reason message fragments for the composition-tile
 * empty guard — the SSOT for the wording. */
const EMPTY_COMPOSITION_MESSAGES = {
  donut: {
    'no-slices':
      'no drawable slices — every row resolved to a null category or a non-numeric value (or the dataset has 0 rows)',
    'zero-total': 'the slice values sum to 0 — every share is 0% and the centre KPI reads 0',
  },
  treemap: {
    'no-slices': 'no drawable leaves — every row resolved to a non-finite value (or the dataset has 0 rows)',
    'zero-total': 'the leaf values sum to 0 — every rectangle has zero area',
  },
} as const;

export type CompositionTileKind = keyof typeof EMPTY_COMPOSITION_MESSAGES;

export type EmptyCompositionReason = keyof (typeof EMPTY_COMPOSITION_MESSAGES)['donut'];

/** Per-tile recovery hints for {@link EmptyCompositionError}. The donut
 * hint additionally teaches the wide→long fold (its categories often
 * arrive as separate value columns on one wide KPI row). */
const EMPTY_COMPOSITION_HINTS: Record<CompositionTileKind, string> = {
  donut:
    'A donut consumes exactly one row per category with one numeric value column — aggregate (group-by + count/sum) down to that shape before charting. If the categories are separate value columns on one wide row, fold it long first and relabel by comparing the key column to the melted column NAME as a plain string (e.g. "oil_now"), never as a keyword. Verify the value column is numeric and non-zero, or drop the tile.',
  treemap:
    'A treemap consumes exactly one row per rectangle with one finite numeric value column — aggregate (group-by + count/sum) down to that shape before charting. Verify the value column is numeric and non-zero, or drop the tile.',
};

/**
 * Thrown at dashboard-eval time when a composition tile (donut,
 * treemap) resolves to nothing the chart can draw: every row dropped
 * by the tile's row predicate (including a 0-row resolve), or the
 * drawable values sum to 0. A composition tile does not look broken
 * when empty — a donut renders a ring with a confident `0` centre KPI,
 * a treemap a blank canvas — so it must fail validation instead of
 * rendering. SSOT for the empty-composition wording —
 * `validateDashboardSpec` throws it at execute time so the agent
 * reshapes the data before claiming success.
 */
export class EmptyCompositionError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly tile: CompositionTileKind,
    readonly subject: string,
    readonly reason: EmptyCompositionReason,
    pos: SourcePos,
  ) {
    super(`${subject}: ${EMPTY_COMPOSITION_MESSAGES[tile][reason]}`, pos);
    this.staticHint = EMPTY_COMPOSITION_HINTS[tile];
  }
}

/**
 * Thrown at dashboard-eval time when two or more `(series …)` in a
 * `(cartesian …)` tile map to the SAME column reference (same dataset,
 * column, and struct path). Each series must plot a distinct column;
 * when several share one column the renderer keys merged points by x
 * with last-write-wins, so every sharing series collapses onto a single
 * identical line — a silent mis-render the agent cannot otherwise see.
 * The fix is to reshape the data so each series owns its column:
 * `unnest` to expand CDF datapoint-array columns into long rows, then
 * `pivot` (long→wide, one column per group). SSOT for the
 * duplicate-series wording — `validateDashboardSpec` throws it at
 * execute time so the agent self-corrects before claiming success.
 */
export class DuplicateSeriesError extends EvaluationError {
  constructor(
    readonly duplicates: readonly string[],
    pos: SourcePos,
  ) {
    super(
      `cartesian: ${duplicates.length} series reference the same column (${duplicates
        .map((c) => `\`${c}\``)
        .join(', ')}) — each series must plot a distinct column, otherwise they render as identical overlapping lines`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'If the dataset is in long format — one row per (x, group), e.g. one row per (day, well) with a discriminator column — reshape it to wide with `(pivot :rows [:<x>] :cols [:<group>] :values (<agg> :<value>))` so each group becomes its own column.',
      'If a column holds CDF datapoint arrays (one row per series), expand it first with `(unnest …)` into long rows, then `(pivot …)` to wide.',
      'After reshaping, point each `(series …)` `:y` at one of the resulting per-group columns.',
    ];
  }
}
/**
 * Thrown at dashboard-eval time when one `(series …)` carries more than
 * one datapoint at the same `:x` value. A `line` / `area` / `bar` series
 * is a function of x — `CartesianPanel` merges its points keyed by x with
 * last-write-wins, so two rows at one x silently drop all but an arbitrary
 * survivor (e.g. a 30-day trend that plots one well's value per day and
 * mislabels it a total). The cause is almost always un-aggregated
 * long-form data: many series (metric groups, assets, roles) share each
 * (x) bucket. The fix is to collapse to one row per x — aggregate across
 * the group dimension, or pivot the discriminator to columns and plot one
 * series per column. `scatter` is exempt (a point cloud is many y per x by
 * design). SSOT for the non-unique-x wording — `validateDashboardSpec`
 * throws it at execute time so the agent self-corrects before claiming
 * success.
 */
export class NonUniqueSeriesXError extends EvaluationError {
  constructor(
    readonly series: string,
    readonly xColumn: string,
    readonly yColumn: string,
    readonly duplicates: readonly string[],
    readonly collisions: number,
    pos: SourcePos,
  ) {
    super(
      `cartesian: series \`${series}\` plots ${collisions} datapoints that share an x value — its \`:x\` column \`${xColumn}\` repeats (e.g. ${duplicates
        .map((x) => `\`${x}\``)
        .join(
          ', ',
        )}${collisions > duplicates.length ? ', …' : ''}). A line/area/bar series is a function of x, so the renderer merges points keyed by x last-write-wins and silently drops all but one per x. The data is long-form: many series share each \`${xColumn}\` bucket, so \`${yColumn}\` is not yet reduced to one value per x.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      `Collapse to one row per \`${this.xColumn}\` before plotting. For a total across the series in scope, aggregate: \`(aggregate :group-by [:${this.xColumn}] :pairs {:${this.yColumn} (sum :${this.yColumn})})\` — use \`mean\` for a rate, \`max\` / \`last\` for a level — then point \`:y\` at \`${this.yColumn}\`.`,
      `To keep the series apart instead of totalling, pivot the discriminator (the column that distinguishes them — valueRole, metricGroup, asset) into columns: \`(pivot :rows [:${this.xColumn}] :cols [:<group>] :values (sum :${this.yColumn}))\`, then plot one \`(series …)\` per resulting column (one chart each when units differ).`,
      `If \`${this.yColumn}\` still holds CDF datapoint arrays (one row per series), \`(unnest …)\` it to (\`${this.xColumn}\`, \`${this.yColumn}\`) rows first, then aggregate or pivot as above.`,
    ];
  }
}
/**
 * Thrown at dashboard-eval time when one `(series …)`'s `:y` values carry
 * two or more DISTINCT per-row units (unit-boxed cells that disagree).
 * A cartesian value axis plots one magnitude scale; points in different
 * units (e.g. GJ/t next to %) are not comparable on it, so the chart
 * would render confidently meaningless geometry. The cause is almost
 * always un-filtered long-form data: rows of several metrics (each with
 * its own stored unit) share the value column. Unit-homogeneous and
 * entirely unitless series pass; plain numbers alongside boxed ones are
 * the engine's documented unit-drop case, not a conflict. SSOT for the
 * mixed-unit wording — `validateDashboardSpec` throws it at execute time
 * so the agent filters or converts before claiming success.
 */
export class MixedUnitSeriesError extends EvaluationError {
  constructor(
    readonly series: string,
    readonly yColumn: string,
    readonly symbols: readonly string[],
    pos: SourcePos,
  ) {
    super(
      `cartesian: series \`${series}\` mixes units on its \`:y\` column \`${yColumn}\` — the resolved values carry ${symbols
        .map((s) => `\`${s}\``)
        .join(', ')}. Points in different units are not comparable on one value axis.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      `Filter to one unit before plotting — keep only the rows of a single metric/measure so \`${this.yColumn}\` is unit-homogeneous.`,
      'To compare the mixed rows, pivot the discriminator (the column distinguishing them — metric, valueRole, …) into columns and plot one `(series …)` per resulting column, one chart per unit; or convert the values to a shared unit first.',
    ];
  }
}
/**
 * Thrown at dashboard-eval time when two or more `(series …)` sharing
 * one y-axis carry DIFFERENT (per-series homogeneous) units. A value
 * axis plots one magnitude scale, so e.g. `°C` next to `bar` on the
 * left axis renders incomparable geometry. Series with internally
 * mixed units are diagnosed by `MixedUnitSeriesError` instead, and
 * unitless series never conflict. SSOT for the cross-series unit
 * wording — `validateDashboardSpec` throws it at execute time so the
 * agent re-axes or converts before claiming success.
 */
export class MixedUnitAxisError extends EvaluationError {
  constructor(
    readonly axis: string,
    readonly members: readonly { label: string; symbol: string }[],
    pos: SourcePos,
  ) {
    super(
      `cartesian: the ${axis} y-axis mixes units across its series — ${members
        .map((m) => `\`${m.label}\` is \`${m.symbol}\``)
        .join(', ')}. One value axis plots one magnitude scale; series in different units are not comparable on it.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'Move one unit to the other y-axis with `:axis right` (or `:axis left`) on that `(series …)` — a dual-axis chart carries one unit per side.',
      'With more than two units, split into one cartesian tile per unit, or convert the values to a shared unit before plotting.',
    ];
  }
}

/**
 * Thrown at dashboard-eval time when a `(cartesian …)` tile has no
 * series with at least two distinct non-null `:x` values. A cartesian
 * chart draws change across x; with a single x bucket per series it
 * renders one dot or bar per series — confident-looking geometry that
 * conveys nothing an x-axis is for. The cause is almost always a query
 * whose granularity matches its window (e.g. monthly values over a
 * 30-day range → one bucket). One dense series alongside sparse ones
 * passes — sparse-vs-dense is a valid comparison. SSOT for the
 * degenerate-x wording — `validateDashboardSpec` throws it at execute
 * time so the agent re-queries or re-tiles before claiming success.
 */
export class InsufficientXError extends EvaluationError {
  constructor(
    readonly counts: readonly { series: string; xColumn: string; distinct: number }[],
    pos: SourcePos,
  ) {
    super(
      `cartesian: no series has two or more distinct \`:x\` values — ${counts
        .map(
          (c) =>
            `series \`${c.series}\` resolves ${c.distinct} distinct value${c.distinct === 1 ? '' : 's'} of \`${c.xColumn}\``,
        )
        .join(
          '; ',
        )}. A cartesian chart draws change across x; a single x bucket renders one dot or bar and shows no trend.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'Re-fetch with more x buckets — a finer granularity (e.g. daily instead of monthly) or a longer window, so each series resolves multiple x values.',
      'If a single value per series is the point, drop the cartesian tile: state each value with a `(metric …)` tile, or compare the series in a `(table …)` or `(donut …)`.',
    ];
  }
}

/**
 * Thrown at dashboard-eval time when a `(band …)` has rows whose
 * `:lower` bound exceeds `:upper`. The band renders inside-out or
 * collapses on those rows, silently misdrawing the envelope. SSOT for
 * the inverted-band wording — `validateDashboardSpec` throws it at
 * execute time so the agent fixes the bounds before claiming success.
 */
export class InvertedBandError extends EvaluationError {
  constructor(
    readonly band: string,
    readonly sample: readonly string[],
    readonly count: number,
    pos: SourcePos,
  ) {
    super(
      `cartesian: band \`${band}\` has ${count} row${count === 1 ? '' : 's'} where \`:lower\` exceeds \`:upper\` (e.g. ${sample.join(', ')}) — the band renders inside-out on those rows.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'If the bounds are swapped everywhere, exchange the `:lower` and `:upper` bindings on the `(band …)`.',
      'If only some rows invert, fix the bound computation — derive the bounds so lower ≤ upper holds row-wise (e.g. min/max of the envelope columns).',
    ];
  }
}

/**
 * Thrown at dashboard-eval time when a composition tile (donut,
 * treemap) would draw a negative value. A slice or leaf is a share of
 * a non-negative whole — negative area has no geometry, so renderers
 * draw garbage or silently drop the row. SSOT for the negative-value
 * wording — `validateDashboardSpec` throws it at execute time so the
 * agent reshapes or re-tiles before claiming success.
 */
export class NegativeCompositionError extends EvaluationError {
  constructor(
    readonly tile: CompositionTileKind,
    readonly subject: string,
    readonly sample: readonly string[],
    readonly count: number,
    pos: SourcePos,
  ) {
    super(
      `${subject}: ${count} drawable value${count === 1 ? ' is' : 's are'} negative (e.g. ${sample.join(', ')}). A ${tile} shows parts of a non-negative whole — negative area has no geometry.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'If the negatives are signed changes or balances, a composition tile is the wrong form — plot them as a `(cartesian …)` bar chart, where sign is meaningful.',
      'If the whole is genuinely non-negative, fix the data: filter out the negative rows or aggregate so each category nets to a non-negative share.',
    ];
  }
}

/**
 * Thrown at dashboard-eval time when a `(donut …)` resolves to exactly
 * one drawable slice — a 100% ring: composition chrome around a single
 * number, with nothing to compose. SSOT for the single-slice wording —
 * `validateDashboardSpec` throws it at execute time so the agent
 * re-tiles or re-groups before claiming success.
 */
export class SingleSliceDonutError extends EvaluationError {
  constructor(
    readonly subject: string,
    readonly slice: string,
    pos: SourcePos,
  ) {
    super(
      `${subject}: only one drawable slice (\`${slice}\`) — a single-category donut renders a 100% ring and shows no composition.`,
      pos,
    );
  }

  override suggestions(): readonly string[] {
    return [
      'State the single value with a `(metric …)` tile instead — a one-slice donut is chrome around one number.',
      'If a breakdown was intended, group by the discriminating column first so several categories reach the donut.',
    ];
  }
}

/**
 * Thrown when a single dashboard tile reads `(col …)` columns from more
 * than one source dataset. A tile renders ONE row-aligned dataset — the
 * `MaterializedDataset` invariant makes its columns equal-length and
 * row-for-row aligned — so columns from different bindings have no shared
 * row order, and combining them silently mispairs rows. The fix is an
 * explicit `(join …)` upstream that produces one aligned dataset the tile
 * can reference. SSOT for the single-dataset wording; thrown by
 * `validateDashboardSpec` before materialisation.
 */
export class MultiDatasetTileError extends EvaluationError {
  protected override readonly staticHint: string;
  constructor(
    readonly tileKind: string,
    /** `[bindingName, columnLabels]` per distinct source dataset, in first-seen order. */
    readonly datasets: readonly (readonly [string, readonly string[]])[],
    pos: SourcePos,
  ) {
    super(
      `${tileKind}: reads columns from ${datasets.length} datasets (${datasets
        .map(([ds, cols]) => `\`${ds}\` → ${cols.join(', ')}`)
        .join(
          '; ',
        )}) — a tile renders one row-aligned dataset, so columns from different datasets have no shared row order`,
      pos,
    );
    const names = datasets.map(([ds]) => ds);
    const [first, ...rest] = names;
    const joinExpr =
      rest.length === 1
        ? `(join ${first} ${rest[0]} :on [:<key>])`
        : `(-> ${first} ${rest.map((n) => `(join ${n} :on [:<key>])`).join(' ')})`;
    this.staticHint =
      `Join the datasets into one before the tile: ${joinExpr} — use the shared key column ` +
      '(e.g. :day), then reference every tile column from the joined result. ' +
      ':type defaults to :inner (matched rows only); use :left or :full to keep unmatched rows as nulls.';
  }
}
